import { Box, useDisclosure } from '@chakra-ui/react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import DealerCardResults from './result-lists/DealerCardResults-v2';
import LicenseNumberResult from './result-lists/LicenseNumberResult-v2';
import TextResults from './result-lists/TextResults';
import { CognitoInterface } from '@/queue/Queue';
import ExploreLandingPage from './result-lists/ExploreLandingPage';
import { batch, useDispatch } from 'react-redux';
import type { AppDispatch } from '@/redux/store';
import { callToAssignTransferDealer, callToAssignTransferLicense } from '@/queue/ducks/slice';
import { getEightDigitLicenseKey } from './ffl-detail/utils.mjs';
import { OutlineToasterNotification } from '@/toaster/successNotification';
import { Toastertype } from '@/queue/types';
import { DEALER_ALREADY_ASSIGNED_ERROR } from '@/utils/constants';

import {
  setLicenseByZipDistance,
  setLicenseByZipQueryParams,
  setQuery,
  callToSuggestedResult,
  callToGetSavedFilterCollections,
  callToUpsertSavedFilterCollection,
  callToDeleteSavedFilterCollection,
  setSavedFilterSuccessEvent,
  setSelectedSavedFilterId,
  setSearchRecognizer,
  setShowDropdownSlice,
  setSuggestedResult,
  setLicenseByState
} from './ducks/slice';
import {
  getIsSearchStarted,
  getIsSearchingDealer,
  getLicenseByState,
  getLicenseByZipDistance,
  getLicenseByZipQueryParams,
  getQuery,
  getSearchCount,
  getSearchRecognizer,
  getSuggestedResult,
  getSuggestedResultLoading,
  getSavedFilterCollections,
  getSavedFilterCollectionsLoading,
  getSelectedSavedFilterId,
  getSavedFilterSuccessEvent
} from './ducks/selectors';
import {
  ZipCodeRecognizer,
  BusinessNameRecognizer,
  PhoneNumberRecognizer,
  FFLRecognizer,
  Recognizer,
  FFLSearchComponent,
  ControlNumberRecognizer,
  EINNumberRecognizer
} from '@masterffl/ffl-search-v2-component';
import { LicenseByZipParamsInterface } from '@/model/explore';
import { useSearchParams } from 'react-router-dom';
import { ShowTransferModel } from '@/queue/elements/QueueGrid';
import ControlNumberResult from './result-lists/ControlNumberResult-v2';
import EINNumberResult from './result-lists/EINNumberResult-v2';
import { removedDashFromNumber } from '@/utils/utils';
import { getIsAssigningTransferDealer } from '@/queue/ducks/selectors';
import { logAnalyticsEvent } from '@/utils/analyticsLogger';
import { analyticsEvents } from '@/utils/analyticsEvents';
import {
  applySavedSearchFiltersToExploreState,
  buildExploreDealerSavedSearchFiltersPayload,
  coerceExploreSavedSearchFiltersFromApi,
  coerceSavedFilterCollectionsToArray,
  getDefaultSavedFilterCollection,
  type ExploreDealerSavedSearchMeta
} from '@/explore/savedFilterCollections';

const MAX_SAVED_DEALER_SEARCH_FILTERS = 50;

const EXPLORER_RECOGNIZER_KIND_TO_CTOR = {
  zip: ZipCodeRecognizer,
  business: BusinessNameRecognizer,
  phone: PhoneNumberRecognizer,
  ffl: FFLRecognizer,
  control: ControlNumberRecognizer,
  ein: EINNumberRecognizer
} as const;

function getExploreRecognizerKind(
  r: Recognizer | undefined
): keyof typeof EXPLORER_RECOGNIZER_KIND_TO_CTOR | null {
  if (!r) return null;
  if (r instanceof ZipCodeRecognizer) return 'zip';
  if (r instanceof BusinessNameRecognizer) return 'business';
  if (r instanceof PhoneNumberRecognizer) return 'phone';
  if (r instanceof FFLRecognizer) return 'ffl';
  if (r instanceof ControlNumberRecognizer) return 'control';
  if (r instanceof EINNumberRecognizer) return 'ein';
  return null;
}

function restoreExploreSavedSearchToRedux(
  dispatch: AppDispatch,
  meta: ExploreDealerSavedSearchMeta
) {
  const Ctor =
    EXPLORER_RECOGNIZER_KIND_TO_CTOR[
      meta.recognizerKind as keyof typeof EXPLORER_RECOGNIZER_KIND_TO_CTOR
    ];
  if (!Ctor || !meta.query?.length) return;
  const recognizer = new Ctor();
  recognizer.match = meta.query;
  dispatch(setQuery(meta.query));
  dispatch(setSearchRecognizer(recognizer));
}

/** Older collections store plain search text on `name` only (no exploreDealerSearchMeta). */
function restoreLegacySearchFromCollectionName(
  dispatch: AppDispatch,
  name: string
): boolean {
  const text = name.trim();
  if (!text) return false;
  const zipCodeRecognizer = new ZipCodeRecognizer();
  if (zipCodeRecognizer.matches(text) && zipCodeRecognizer.match) {
    dispatch(setQuery(zipCodeRecognizer.match));
    dispatch(setSearchRecognizer(zipCodeRecognizer));
    return true;
  }
  const fflRecognizer = new FFLRecognizer();
  if (fflRecognizer.matches(text) && fflRecognizer.match) {
    dispatch(setQuery(fflRecognizer.match));
    dispatch(setSearchRecognizer(fflRecognizer));
    return true;
  }
  const controlNumberRecognizer = new ControlNumberRecognizer();
  if (controlNumberRecognizer.matches(text) && controlNumberRecognizer.match) {
    dispatch(setQuery(controlNumberRecognizer.match));
    dispatch(setSearchRecognizer(controlNumberRecognizer));
    return true;
  }
  const einNumberRecognizer = new EINNumberRecognizer();
  if (einNumberRecognizer.matches(text) && einNumberRecognizer.match) {
    dispatch(setQuery(einNumberRecognizer.match));
    dispatch(setSearchRecognizer(einNumberRecognizer));
    return true;
  }
  const phoneNumberRecognizer = new PhoneNumberRecognizer();
  if (phoneNumberRecognizer.matches(text) && phoneNumberRecognizer.match) {
    dispatch(setQuery(phoneNumberRecognizer.match));
    dispatch(setSearchRecognizer(phoneNumberRecognizer));
    return true;
  }
  const businessNameRecognizer = new BusinessNameRecognizer();
  if (businessNameRecognizer.matches(text) && businessNameRecognizer.match) {
    dispatch(setQuery(businessNameRecognizer.match));
    dispatch(setSearchRecognizer(businessNameRecognizer));
    return true;
  }
  return false;
}

export default function ExploreV2({
  cognitoGroup,
  isSuperAdmin,
  assignNewDealerInitialZipCode,
  isAssignDealerModal,
  assignedDealerLicenseKey,
  onCloseAssignDealerModal,
  transferId,
  type,
  setLicenseKey,
  isShowOnlyNFAFilterDisabled = false
}: CognitoInterface) {
  const query = getQuery();
  const searchRecognizer = getSearchRecognizer();
  const suggestedResult = getSuggestedResult();
  const suggestedResultLoading = getSuggestedResultLoading();
  const [srchRcgnzer, setSrchRcgnzer] = useState(searchRecognizer);
  const dispatch = useDispatch<AppDispatch>();
  const getParams:any = getLicenseByZipQueryParams();
  const getDistance = getLicenseByZipDistance();
  const searchCountChange = getSearchCount();
  const searchStateChange = getIsSearchStarted();
  const [selectedView, setSelectedView] = useState('list');
  const [searchCount, setSearchCount] = useState(searchCountChange);
  const [isSearchStarted, setIsSearchStarted] = useState(searchStateChange);
  const isSearchingDealer = getIsSearchingDealer();
  const [isSearchingDealerData, setIsSearchingDealerData] = useState<boolean>(isSearchingDealer);
  const [queryParams, setQueryParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState<string>("");
  const {
    isOpen: isTransferModalOpen,
    onClose: onCloseTransferModal,
    onOpen: onOpenTransferModal
  } = useDisclosure();
  const showTransferId = queryParams.get('transfer_id');
  const useSort = queryParams.get('useSort');
  const [isResultTextChange, setIsResultTextChange] = useState<boolean>(false);
  const [isAssignLicenseComplete, setIsAssignLicenseComplete] = useState<boolean>(false);
  const suggestedResultRef = useRef<any>(null);
  const defaultSavedFilterAppliedRef = useRef(false);
  const isReassignDealerInProgress = getIsAssigningTransferDealer();
  const licenseByState = getLicenseByState();
  const savedFilters = getSavedFilterCollections();
  const isSavedFiltersLoading = getSavedFilterCollectionsLoading();
  const selectedFilterId = getSelectedSavedFilterId();
  const successEvent = getSavedFilterSuccessEvent();
  const savedFilterType = 'dealerSearch';

  useEffect(() => {
    if(assignNewDealerInitialZipCode || query) {
      assignNewDealerInitialZipCode && dispatch(setQuery([assignNewDealerInitialZipCode]));
      const querySearch = query ?? []
      const searchLicense = assignNewDealerInitialZipCode || querySearch[0]
      const zipCodeRecognizer = new ZipCodeRecognizer();
      const fflRecognizer = new FFLRecognizer();
      const controlNumberRecognizer = new ControlNumberRecognizer();
      const einNumberRecognizer = new EINNumberRecognizer();
      const businessNameRecognizer = new BusinessNameRecognizer();
      const phoneNumberRecognizer = new PhoneNumberRecognizer();
      if (zipCodeRecognizer.matches(searchLicense) && zipCodeRecognizer.match) {       
        handleSearchSubmit(searchLicense, zipCodeRecognizer);
      }
      else if (fflRecognizer.matches(searchLicense) && fflRecognizer.match) {
        handleSearchSubmit(searchLicense, fflRecognizer);
      }
      else if (controlNumberRecognizer.matches(searchLicense) && controlNumberRecognizer.match) {
        handleSearchSubmit(searchLicense, controlNumberRecognizer);
      }
      else if (einNumberRecognizer.matches(searchLicense) && einNumberRecognizer.match) {
        handleSearchSubmit(searchLicense, einNumberRecognizer);
      }
      else if (phoneNumberRecognizer.matches(searchLicense) && phoneNumberRecognizer.match) {
        handleSearchSubmit(searchLicense, phoneNumberRecognizer);
      }
      else if (businessNameRecognizer.matches(searchLicense) && businessNameRecognizer.match) {
        handleSearchSubmit(searchLicense, businessNameRecognizer);
      }
    }
    document.body.setAttribute('class', 'explore-page');
    return () => {
      document.body.removeAttribute('class');
    };
  }, []);

  useEffect(() => {
    dispatch(callToGetSavedFilterCollections({ type: savedFilterType }));
  }, []);

  useEffect(() => {
    if (isSavedFiltersLoading || defaultSavedFilterAppliedRef.current) return;
    const defaultItem = getDefaultSavedFilterCollection(savedFilters);
    if (!defaultItem) return;
    defaultSavedFilterAppliedRef.current = true;
    const id = defaultItem.id != null ? String(defaultItem.id) : null;
    if (id) dispatch(setSelectedSavedFilterId(id));
    const searchFilters = defaultItem.search_filters ?? defaultItem.searchFilters;
    const meta = applySavedSearchFiltersToExploreState(dispatch, searchFilters);
    if (meta) restoreExploreSavedSearchToRedux(dispatch, meta);
    else {
      const legacyName = typeof defaultItem.name === 'string' ? defaultItem.name : '';
      restoreLegacySearchFromCollectionName(dispatch, legacyName);
    }
  }, [isSavedFiltersLoading, savedFilters, dispatch]);
  useEffect(() => {
    setIsSearchStarted(searchStateChange)
  }, [searchStateChange])
  
  // useEffect(() => {
  //   if (searchCountChange) {
  //     searchCountChange == -1 ? setSearchCount(0) : setSearchCount(searchCountChange);
  //   }
  // }, [searchCountChange]);

  useEffect(() => {
    if (searchCountChange !== undefined) { 
      setSearchCount(searchCountChange === -1 ? 0 : searchCountChange);
    }
  }, [searchCountChange]);

  useEffect(() => {
    setIsSearchingDealerData(isSearchingDealer);
  }, [isSearchingDealer]);

  useEffect(() => {
    setSrchRcgnzer(searchRecognizer);
  }, [searchRecognizer]);

  useEffect(() => {
    if (window) {
      const transferId = queryParams.get('transfer_id');
      if (transferId) onOpenTransferModal();
    }
  }, [queryParams]);
  useEffect(() => {
    if(!isReassignDealerInProgress && isAssignLicenseComplete) {
      onCloseAssignDealerModal && onCloseAssignDealerModal()
      setIsAssignLicenseComplete(false)
    }
  }, [isReassignDealerInProgress])
  
  function handleSearchSubmit(searchString: string, recognizer: Recognizer) {
    logAnalyticsEvent(analyticsEvents.searchDealers)
    dispatch(setQuery(recognizer.match));
    dispatch(setSearchRecognizer(recognizer));
    setIsResultTextChange(false);
  }

  const setPageNumber = (number:number) => {
    setIsResultTextChange(number > 0 ? true : false);
  }
  const onDealerAssigned = (dealer:any, reason?:any) => {

    setIsAssignLicenseComplete(true)
    const dealerId = dealer.licenses.filter((license:any) => license.isPrimaryForInbound).map((license:any) => license.licenseKey)[0];
    const selectedDealerLicenseKey = getEightDigitLicenseKey(dealerId);
    if(type === 'initial-transfer') dispatch(callToAssignTransferLicense({ licenseKey: selectedDealerLicenseKey}));

    else {
      assignedDealerLicenseKey = assignedDealerLicenseKey ? getEightDigitLicenseKey(assignedDealerLicenseKey) : '';
      dispatch(callToAssignTransferDealer({ transferId: transferId as string, licenseKey: selectedDealerLicenseKey, reason: reason.trim()}));
    }
  }

const searchQueryParams = (query:string[] | undefined)=>{
  let formattedSearchQuery = '';
  if (query){
    if (srchRcgnzer instanceof ZipCodeRecognizer) formattedSearchQuery = query[1]
    else if (srchRcgnzer instanceof FFLRecognizer) formattedSearchQuery = query[1] + query[2] + query[query.length-1]
    else if (srchRcgnzer instanceof ControlNumberRecognizer) formattedSearchQuery = removedDashFromNumber(query[0])
    else if (srchRcgnzer instanceof EINNumberRecognizer) formattedSearchQuery = removedDashFromNumber(query[0])
    else if (srchRcgnzer instanceof BusinessNameRecognizer) formattedSearchQuery = query[0]
    else if (srchRcgnzer instanceof PhoneNumberRecognizer) formattedSearchQuery = removedDashFromNumber(query[0])
    else formattedSearchQuery = query[0]
  }  
  return formattedSearchQuery
}
  /**
   * Render a search results list depending on the query type
   */
  const setDealerLicense = (dealer:any) => {
    const dealerId = dealer.licenses.filter((license:any) => license.isPrimaryForInbound).map((license:any) => license.licenseKey)[0];
    const selectedDealerLicenseKey = getEightDigitLicenseKey(dealerId);
    setLicenseKey && setLicenseKey(selectedDealerLicenseKey)
    onCloseAssignDealerModal && onCloseAssignDealerModal()
  }
  function renderSwitch() {
    document.body.setAttribute('class', 'explore-page');
    if (query) {
      if (srchRcgnzer instanceof BusinessNameRecognizer || srchRcgnzer instanceof ZipCodeRecognizer || srchRcgnzer instanceof FFLRecognizer || srchRcgnzer instanceof ControlNumberRecognizer || srchRcgnzer instanceof EINNumberRecognizer || srchRcgnzer instanceof PhoneNumberRecognizer) {
        return (
          <div>
            <DealerCardResults
              query={searchQueryParams(query)}
              cognitoGroup={cognitoGroup}
              isSuperAdmin={isSuperAdmin}
              searchRecognizer={srchRcgnzer}
              selectedView={selectedView}
              setNewPageNumber={setPageNumber}
              isFFLSelect={isAssignDealerModal}
              onDealerAssigned={type === "admin-approval" ? setDealerLicense : onDealerAssigned}
              type={type}
              isShowOnlyNFAFilterDisabled={isShowOnlyNFAFilterDisabled}
              useSort={useSort}
            />
          </div>       
        );
      }
      else {
        return <TextResults query={searchQueryParams(query)} />;
      }
    }
  }

  /**
   * Render a variable form layout based on whether a query is submitted or not
   */
  const onChangeDistance = (distance: number) => {
    dispatch(setLicenseByZipDistance(String(distance)));
    setIsResultTextChange(false);
  };
  const onSortOptionsChange = (
    licenseByZipQueryParams: LicenseByZipParamsInterface
  ) => {
    const incomingKeys = Object.keys(licenseByZipQueryParams || {});
    const currentKeys = Object.keys(getParams || {});
    const isIncomingDefault = incomingKeys.length === 1 && licenseByZipQueryParams?.sortBy === 'preferred';
    const isCurrentDefault = currentKeys.length === 1 && (getParams as any)?.sortBy === 'preferred';
    if (isIncomingDefault && isCurrentDefault) return;
    dispatch(setLicenseByZipQueryParams(licenseByZipQueryParams));
    setIsResultTextChange(false);
  };
  const onChangeMapView = (type: string) => {
    type === 'map' ?logAnalyticsEvent(analyticsEvents.mapView): logAnalyticsEvent(analyticsEvents.cardView);
    setSelectedView(type);
  };
  const onCloseTransferModalHandler = () => {
    onCloseTransferModal();
    queryParams.delete('transfer_id');
    setQueryParams(queryParams);
  };

  useEffect(() => {
    const notFound = document.getElementById('not-found-section');
    const height = `calc(100vh - 369px)`
    if (notFound) {
      notFound.style.minHeight = `${height} !important`
    }
  }, [])

  useEffect(() => {
    if(searchQuery && searchQuery?.length >= 4 && (/^(\d{1,2})-?(\d{1,3})?-?(\d{1,5})?-?(\d{1,2})?-?([A-Za-z0-9]{1,2})?-?(\d{1,5})?$/).test(searchQuery) && searchQuery.includes("-")){
      if(suggestedResultRef.current) clearTimeout(suggestedResultRef.current);
      suggestedResultRef.current = setTimeout(() => {
        dispatch(callToSuggestedResult(searchQuery));
      }, 600);
    } else {
      if(suggestedResultRef.current) clearTimeout(suggestedResultRef.current);
      dispatch(setSuggestedResult(null));
    }
  }, [searchQuery]);

  const onStateChange = (states: string[]) => {
    dispatch(setLicenseByState(states));
    setIsResultTextChange(false);
  };

  const getParamsWithState = () => {
    const { state, ...params } = getParams;
    if(licenseByState?.length) return {...params, state: licenseByState}
    return params
  }

  const hasActiveFilters = Boolean(query?.length || Object.keys(getParamsWithState() || {}).length);
  const currentFilter = useMemo(() => {
    const paramsWithState = getParamsWithState() as Record<string, unknown>;
    const kind = getExploreRecognizerKind(srchRcgnzer);
    const searchMeta =
      query?.length && kind ? { query, recognizerKind: kind } : null;
    return buildExploreDealerSavedSearchFiltersPayload(paramsWithState, searchMeta);
  }, [getParams, licenseByState, query, srchRcgnzer]);

  const onSuccessAcknowledge = () => {
    dispatch(setSavedFilterSuccessEvent(null));
  };

  const onSelectSavedFilter = (filter: {
    id: string | number;
    name?: string;
    searchFilters?: unknown;
    search_filters?: unknown;
  }) => {
    const raw = filter.search_filters ?? filter.searchFilters;
    batch(() => {
      dispatch(setSelectedSavedFilterId(filter?.id != null ? String(filter.id) : null));
      const meta = applySavedSearchFiltersToExploreState(dispatch, raw);
      if (meta) restoreExploreSavedSearchToRedux(dispatch, meta);
      else {
        const legacyName = typeof filter.name === 'string' ? filter.name : '';
        if (!restoreLegacySearchFromCollectionName(dispatch, legacyName)) {
          dispatch(setQuery(undefined));
          dispatch(setSearchRecognizer(undefined));
        }
      }
    });
    setIsResultTextChange(false);
  };

  const onSaveFilter = (payload: { name: string; searchFilters: unknown }) => {
    dispatch(
      callToUpsertSavedFilterCollection({
        name: payload.name,
        search_filters: payload.searchFilters as Record<string, unknown>,
        is_default: false,
        type: savedFilterType
      })
    );
  };

  const onUpdateFilter = (payload: {
    id: string;
    name: string;
    searchFilters: unknown;
    isDefault: boolean;
  }) => {
    dispatch(
      callToUpsertSavedFilterCollection({
        id: payload.id,
        name: payload.name,
        search_filters: payload.searchFilters as Record<string, unknown>,
        is_default: payload.isDefault,
        type: savedFilterType
      })
    );
  };

  const onDeleteFilter = (payload: { id: string; name: string }) => {
    dispatch(
      callToDeleteSavedFilterCollection({
        id: payload.id,
        type: savedFilterType,
        name: payload.name
      })
    );
  };

  const onSaveAsDefaultFilter = (payload: {
    id: string;
    name: string;
    searchFilters: unknown;
  }) => {
    dispatch(
      callToUpsertSavedFilterCollection({
        id: payload.id,
        name: payload.name,
        search_filters: payload.searchFilters as Record<string, unknown>,
        is_default: true,
        type: savedFilterType
      })
    );
  };

  const onClearAllFilters = () => {
    dispatch(setSelectedSavedFilterId(null));
  };

  const savedFiltersNormalized = useMemo(
    () =>
      coerceSavedFilterCollectionsToArray(savedFilters).map((f: any) => {
        const raw = f.search_filters ?? f.searchFilters;
        const searchFilters =
          coerceExploreSavedSearchFiltersFromApi(raw) ??
          (raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {});
        return {
          id: f.id,
          name: f.name,
          isDefault: f.is_default ?? f.isDefault,
          searchFilters
        };
      }),
    [savedFilters]
  );

  const dealerMultiFilterProps = {
    placeholder: 'Select a saved filter...',
    isDisabled: false,
    isLoading: isSavedFiltersLoading,
    maxSavedFilters: MAX_SAVED_DEALER_SEARCH_FILTERS,
    hasActiveFilters,
    currentFilter,
    savedFilters: savedFiltersNormalized,
    selectedFilterId,
    successEvent,
    onSuccessAcknowledge,
    onSelectSavedFilter,
    onSaveFilter,
    onUpdateFilter,
    onDeleteFilter,
    onSaveAsDefaultFilter,
    onClearAllFilters
  };

  function renderForm() {
    if (query) {
      return (
        <>
          <FFLSearchComponent
            placeholder={'Select a saved filter...'}
            isDisabled={false}
            isLoading={isSavedFiltersLoading}
            maxSavedFilters={MAX_SAVED_DEALER_SEARCH_FILTERS}
            hasActiveFilters={hasActiveFilters}
            currentFilter={currentFilter}
            savedFilters={savedFilters}
            selectedFilterId={selectedFilterId}
            successEvent={successEvent}
            onSuccessAcknowledge={onSuccessAcknowledge}
            onSelectSavedFilter={onSelectSavedFilter}
            onSaveFilter={onSaveFilter}
            onUpdateFilter={onUpdateFilter}
            onDeleteFilter={onDeleteFilter}
            onSaveAsDefaultFilter={onSaveAsDefaultFilter}
            onClearAllFilters={onClearAllFilters}
            searchCount={searchCount}
            isSearchStarted={isSearchStarted}
            isSearchingDealer={isSearchingDealerData}
            onChangeMapView={onChangeMapView}
            inputQuery={query}
            inputDistance={getDistance}
            searchRecognizer={srchRcgnzer}
            searchParams={getParamsWithState()}
            handleSearchSubmit={handleSearchSubmit}
            onChangeDistance={onChangeDistance}
            onSortOptionsChange={onSortOptionsChange}
            filterConfiguration={{}}
            isSearchingTextChanged={isResultTextChange}
            setSearchQuery={setSearchQuery}
            suggestedResult={suggestedResult}
            suggestedResultLoading={suggestedResultLoading}
            isMapBUttonDisable={Boolean(isAssignDealerModal)}
            onStateChange={onStateChange}
            checkShowOnlyNFAFilterDisabled={isShowOnlyNFAFilterDisabled}
            dealerMultiFilterProps={dealerMultiFilterProps}
          />
          {renderSwitch()}
        </>
      );
    } else {
      return (
      
        <>
          <FFLSearchComponent
            placeholder={'Select a saved filter...'}
            isDisabled={false}
            isLoading={isSavedFiltersLoading}
            maxSavedFilters={MAX_SAVED_DEALER_SEARCH_FILTERS}
            hasActiveFilters={hasActiveFilters}
            currentFilter={currentFilter}
            savedFilters={savedFilters}
            selectedFilterId={selectedFilterId}
            successEvent={successEvent}
            onSuccessAcknowledge={onSuccessAcknowledge}
            onSelectSavedFilter={onSelectSavedFilter}
            onSaveFilter={onSaveFilter}
            onUpdateFilter={onUpdateFilter}
            onDeleteFilter={onDeleteFilter}
            onSaveAsDefaultFilter={onSaveAsDefaultFilter}
            onClearAllFilters={onClearAllFilters}
            searchCount={searchCount}
            isSearchStarted={isSearchStarted}
            onChangeMapView={onChangeMapView}
            inputQuery={query}
            inputDistance={getDistance}
            searchRecognizer={srchRcgnzer}
            searchParams={getParams}
            handleSearchSubmit={handleSearchSubmit}
            onChangeDistance={onChangeDistance}
            onSortOptionsChange={onSortOptionsChange}
            filterConfiguration={{}}
            isSearchingTextChanged={isResultTextChange}
            setSearchQuery={setSearchQuery}
            suggestedResult={suggestedResult}
            suggestedResultLoading={suggestedResultLoading}
            isMapBUttonDisable={Boolean(isAssignDealerModal)}
            onStateChange={onStateChange}
            checkShowOnlyNFAFilterDisable={isShowOnlyNFAFilterDisabled}
            dealerMultiFilterProps={dealerMultiFilterProps}

          />
          <ExploreLandingPage hideText={true} />
        </>
      );
    }
  }

  return (
    <Box className={'Explore explore-container'}>
      {isTransferModalOpen && (
        <ShowTransferModel
          isOpen={isTransferModalOpen}
          onClose={onCloseTransferModalHandler}
          transfer={{ id: showTransferId as string }}
          reFetchData={() => {}}
          isSuperAdmin={isSuperAdmin}
          isStoreAdmin={false}
          cognitoGroup={false}
        />
      )}
      <div>
        {renderForm()}
      </div>
    </Box>
  );
}
